
    import Vue from 'vue';
    import Component from 'vue-class-component';
    import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
    import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
    import {Prop} from 'vue-property-decorator';
    import {
        InvalidationRequest,
        EndorsementsClient,
        EndorsementStatus,
        EndorsementDetailsModel,
        IInvalidationRequest,
        IEndorsementActions,
        ManualInvalidationReason,
        ISuspendRequest,
        SuspendRequest,
        SuspensionReason,
        EndorsementActions,
        MinimalDocumentModel
    } from "@/api-client";
    import DetailsLayout from "@/features/personProfile/components/DetailsLayout.vue";
    import EndorsementDetailsContent from "@/features/endorsements/EndorsementDetailsContent.vue";
    import ApprovalActions from "@/features/shared/components/sections/ApprovalActions.vue";
    import SuspensionDetails from "@/features/endorsements/components/SuspensionDetails.vue";
    import RejectionDetails from "@/features/shared/components/RejectionDetails.vue";
    import toasts from "@/components/Toasts";
    import LabelOptional from "@/features/shared/components/LabelOptional.vue";
    import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
    import {IApprovalActions} from "@/features/shared/components/sections/ApprovalActions";
    import AdditionalInfoButton from "@/features/shared/components/AdditionalInfoButton.vue";
    import DuplicationDetails from "@/features/shared/components/DuplicationDetails.vue";
    import DocumentValidity from "@/features/documents/query/components/DocumentValidity.vue";
    import MinimalDocumentDetails from "@/features/documents/MinimalDocumentDetails.vue";
    import {NavigationGuardNext} from "vue-router/types/router";
    import ReplacementDetails from "@/features/shared/components/ReplacementDetails.vue";
    import DocumentDigitalRepresentationModal from "@/features/shared/components/DocumentDigitalRepresentationModal.vue";
    import renderEndorsementPrintTemplate from "@/features/shared/EndorsementPrintService";
    import QrCode from "@/components/QrCode.vue";
    import DocumentValidityQueryTimestamp from "@/features/shared/components/DocumentValidityQueryTimestamp.vue";
    import waitForAllImagesLoaded from "@/components/WaitForImagesLoaded";

    const accessTokenStorageKey = 'mis-endorsement-access-token';

    interface Props {
        preloadedEndorsement: MinimalDocumentModel | EndorsementDetailsModel;
        id?: string;
        preLoadedActions: EndorsementActions;
        preLoadedAdditionalInfo?: string;
        accessToken?: string;
    }

    @Component({
        components: {
            QrCode,
            DetailsLayout,
            SectionPageHeading,
            HeadingGroup,
            EndorsementDetailsContent,
            ApprovalActions,
            SuspensionDetails,
            RejectionDetails,
            LabelOptional,
            ButtonWithConfirm,
            AdditionalInfoButton,
            DuplicationDetails,
            DocumentValidity,
            MinimalDocumentDetails,
            ReplacementDetails,
            DocumentDigitalRepresentationModal,
            DocumentValidityQueryTimestamp
        },
        async beforeRouteEnter(to, from, next: NavigationGuardNext<EndorsementDetails>) {
            const props = to.meta!.props as Props;
            try {
                // This code is duplicated in MedicalCertificate.vue, CertificateDetails.vue and EndorsementDetails.vue.
                if (to.params.accessToken) {
                    // Save token to session storage and replace location without token to prevent token from being preserved in history
                    const urlWithoutToken = to.fullPath.replace(to.params.accessToken, "");
                    sessionStorage.setItem(accessTokenStorageKey, to.params.accessToken as string);
                    await window.location.replace(urlWithoutToken); // TODO(KK): Siin ei call'ita next'i.
                    return;
                }

                const accessToken = sessionStorage.getItem(accessTokenStorageKey);
                // Access token is not removed from session storage to enable language change and login when querying endorsement by access token.

                if (!to.params.endorsementId && !to.params.id && !accessToken) {
                    await this.$router.push({ name: "not-found" });
                    return;
                }

                // Either id OR access token is required
                const endorsement = to.params.endorsementId || to.params.id
                    ? await new EndorsementsClient().details(to.params.endorsementId || to.params.id)
                    : await new EndorsementsClient().detailsByAccessToken(accessToken!);

                props.preloadedEndorsement = endorsement;
                props.id = endorsement.id;

                // Only assign if no id was given to be able to determine whether the query was a public or a private one.
                props.accessToken = !to.params.endorsementId && !to.params.id ? accessToken! : "";

                props.preLoadedActions = await new EndorsementsClient().availableActions(endorsement.id);
                if(props.preLoadedActions.canSetAdditionalInfo){
                    props.preLoadedAdditionalInfo = await new EndorsementsClient().additionalInfo(endorsement.id);
                }

                next();
            } catch(e) {
                next(e);
            }
        }
    })
    export default class EndorsementDetails extends Vue implements Props {
        static accessTokenStorageKey = accessTokenStorageKey;

        @Prop({required: true})
        preloadedEndorsement!: MinimalDocumentModel | EndorsementDetailsModel;
        @Prop({default: ""})
        id!: string;
        @Prop({ required: true })
        preLoadedActions!: EndorsementActions;
        @Prop({default: ""})
        preLoadedAdditionalInfo!: string;
        @Prop({default: true})
        addContainerCss!: boolean;
        @Prop({default: ""})
        accessToken!: string;

        endorsement: MinimalDocumentModel | EndorsementDetailsModel = this.preloadedEndorsement;
        actions: IEndorsementActions = this.preLoadedActions;
        additionalInfo = this.preLoadedAdditionalInfo;

        $refs!: {
            digitalRepresentationModal: DocumentDigitalRepresentationModal;
            qrCode: QrCode;
        }

        async refresh() {
            this.endorsement = await new EndorsementsClient().details(this.endorsement.id);
            this.actions = await new EndorsementsClient().availableActions(this.id);
        }

        get approvalActions() : IApprovalActions {
            return {
                canApprove: this.actions.canCancelSuspension,
                canDisapprove: this.actions.canInvalidate,
                canSuspend: this.actions.canSuspend
            }
        }

        async suspend(comment: string, reason: SuspensionReason, startedOn: Date, validUntil: Date | null) {
            const suspendRequest: ISuspendRequest = {
                reason: reason,
                comment: comment,
                startedOn: startedOn,
                validUntil: validUntil
            }
            await new EndorsementsClient().suspend(this.endorsement.id, new SuspendRequest(suspendRequest));
            await this.refresh();
        }

        get showSuspensionDetails(): boolean {
            if(!(this.endorsement instanceof EndorsementDetailsModel))
                return false;
            if (this.endorsement.status === EndorsementStatus.Invalid)
                return false;

            const suspensionDetails = this.endorsement.suspensionDetails;
            if (suspensionDetails == null)
                return false;

            return !!suspensionDetails && !suspensionDetails.isExpired; // Show if suspension is not expired
        }

        async invalidate(comment: string, reason: ManualInvalidationReason) {
            const invalidationRequest: IInvalidationRequest = {reason: reason, comment: comment}
            await new EndorsementsClient().invalidate(this.endorsement.id, new InvalidationRequest(invalidationRequest));
            await this.refresh();
        }

        async cancelSuspension() {
            await new EndorsementsClient().cancelSuspension(this.endorsement.id);
            await this.refresh();
        }

        async setAdditionalInfo() {
            new EndorsementsClient()
                .setAdditionalInfo(this.id, this.additionalInfo)
                .then(() => toasts.success(this.$ls.Global_Toast_Success_AdditionalInfoSaved));
        }

        get areFullDetailsShown(){
            return this.endorsement instanceof EndorsementDetailsModel;
        }

        get isPublicQuery(): boolean {
            return !!this.accessToken;
        }

        async print() {
            const printWindow = window.open()!;
            printWindow.document.open();
            printWindow.document.write(await this.getRenderedPrintTemplate());
            printWindow.document.close();
            printWindow.focus();
            await waitForAllImagesLoaded(printWindow);
            printWindow.print();
        }

        async openDigitalRepresentation() {
            this.$refs.digitalRepresentationModal.show(await this.getRenderedPrintTemplate(true));
        }

        async getRenderedPrintTemplate(transparentSignatures = false) {
            if (!this.areFullDetailsShown)
                throw Error("Only full details can be printed.");

            const printTemplate = this.isPublicQuery
                ? (await new EndorsementsClient().printTemplateByAccessToken(this.accessToken))
                : (await new EndorsementsClient().printTemplate(this.endorsement.id));
            const signatureUrls = transparentSignatures
                ? {
                    transparentSignatureUrl: printTemplate.transparentSignatureUrl,
                    transparentOfficialSignatureUrl: printTemplate.transparentOfficialSignatureUrl
                }
                : {};
            return await renderEndorsementPrintTemplate(this.endorsement as EndorsementDetailsModel, printTemplate.url, this.$refs.qrCode.getBase64QrCode(), printTemplate.frontUrl, printTemplate.backUrl, signatureUrls);
        }
    }
